import { useEffect, useState } from "react";
import {
  ApiError,
  changePassword,
  readCredentialLink,
  setPasswordFromLink,
  type CredentialLink,
} from "./api/client";
import { TextField } from "./components/TextField";

/**
 * Setting a password for the first time — M9-0.
 *
 * **There is no prototype screen for this, and that is not an omission.** Neither the
 * sign-in nor the registration panel has one either: `design/prototype/screens/` draws no
 * door at all. All three are built against the `.signin__*` block in `app.css`, so this is
 * the third panel in an existing family rather than a layout invented for it.
 *
 * The link arrives in the URL fragment — `/set-password#<token>` — because a fragment is
 * never sent to a server, so the credential stays out of access logs and `Referer` headers.
 * The browser reads it and presents it as a bearer token, which is the only moment it
 * crosses the wire.
 *
 * **Setting a password does not sign anybody in.** Every refusal about who may come through
 * the door — the role, an auditor's invitation, the certification body's status — lives in
 * `POST /auth/token`, and a second way in would be a second place for those three to be
 * decided. So this ends by sending them to the sign-in they already have.
 *
 * `Forced` is the same screen with no token at all — M9-0 §5. An account that predates this
 * work signs in with the password Complao issued it, the door refuses with a 403 rather than
 * a token, and the panel arrives here already holding the address and the password that was
 * typed. Same fields, same floor, a different route on submit.
 */
export function SetPassword({ token, onDone }: { token: string; onDone: () => void }) {
  const [link, setLink] = useState<CredentialLink | null>(null);
  // Distinguishes "still asking" from "asked, and it cannot be used" — a screen that
  // renders a refusal while it is still loading tells the reader something untrue.
  const [refusal, setRefusal] = useState<Problem | null>(null);

  useEffect(() => {
    let current = true;
    readCredentialLink(token)
      .then((answer) => current && setLink(answer))
      .catch(
        (cause) =>
          current &&
          setRefusal(
            cause instanceof ApiError
              ? { title: cause.problem.title, detail: cause.problem.detail }
              : { title: "This link could not be checked", detail: "Try again in a moment." },
          ),
      );
    return () => {
      current = false;
    };
  }, [token]);

  return (
    <main className="signin">
      <div className="signin__panel">
        <Brand />
        {refusal ? (
          <Refused refusal={refusal} onDone={onDone} />
        ) : link ? (
          <Form link={link} token={token} onDone={onDone} />
        ) : (
          <p className="signin__tagline">Checking this link…</p>
        )}
      </div>
    </main>
  );
}

interface Problem {
  title: string;
  detail?: string;
}

/**
 * The four endings a link can have, and each says what to do next.
 *
 * Being specific here is safe, and it is not the same decision as the door's deliberate
 * vagueness: the reader is holding a capability Complao sent to their address, so naming
 * what happened to it tells them nothing they did not already have. *Expired* and *already
 * used* need different actions from the person reading them.
 */
function Refused({ refusal, onDone }: { refusal: Problem; onDone: () => void }) {
  return (
    <>
      <p className="problem" role="alert">
        <strong>{refusal.title}</strong>
        {refusal.detail ? <> {refusal.detail}</> : null}
      </p>
      <div className="signin__actions">
        <button className="btn" type="button" onClick={onDone}>
          Back to sign in
        </button>
      </div>
    </>
  );
}

function Form({
  link,
  token,
  onDone,
}: {
  link: CredentialLink;
  token: string;
  onDone: () => void;
}) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const minimum = link.minimum_password_length;
  // Checked here so the reader is told before submitting, and again on the server because
  // requiredness only the browser enforces is requiredness a browser can skip.
  const tooShort = password.length > 0 && password.length < minimum;
  const mismatch = again.length > 0 && again !== password;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < minimum || password !== again) return;
    setBusy(true);
    try {
      setDone(await setPasswordFromLink(token, password));
    } catch (cause) {
      // The server's sentence, not a rewritten one: "that is the current password" and
      // "this link has already been used" are different things to be told.
      setError(cause instanceof ApiError ? cause.message : "The password could not be set.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <p className="callout green" role="status">
          {done}
        </p>
        <div className="signin__actions">
          <button className="btn" type="button" onClick={onDone}>
            Sign in
          </button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <p className="signin__tagline">
        {link.purpose === "password_reset"
          ? "Choose a new password for "
          : "Set a password for "}
        <strong>{link.email}</strong>
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <TextField
        id="password"
        label="New password"
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        secret
        required
        hint={`At least ${minimum} characters.`}
        error={tooShort ? `At least ${minimum} characters.` : undefined}
      />
      <TextField
        id="password-again"
        label="New password again"
        value={again}
        onChange={setAgain}
        autoComplete="new-password"
        secret
        required
        error={mismatch ? "The two passwords do not match." : undefined}
      />

      <div className="signin__actions">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Setting…" : "Set password"}
        </button>
      </div>
    </form>
  );
}

/**
 * The third mode: the door refused, and the account has to choose a password first.
 *
 * There is no link to look up, so nothing is fetched and nothing can be expired. What it
 * has instead is the credential the door just proved — the address and the password that
 * was typed — which is what `POST /auth/password-change` authenticates against.
 */
export function ForcedPasswordChange({
  email,
  currentPassword,
  reason,
  onDone,
}: {
  email: string;
  currentPassword: string;
  reason: string;
  onDone: () => void;
}) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const minimum = MINIMUM_PASSWORD_LENGTH;
  const tooShort = password.length > 0 && password.length < minimum;
  const mismatch = again.length > 0 && again !== password;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < minimum || password !== again) return;
    setBusy(true);
    try {
      setDone(await changePassword(email, currentPassword, password));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The password could not be set.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <div className="signin__panel">
        <Brand />
        {done ? (
          <>
            <p className="callout green" role="status">
              {done}
            </p>
            <div className="signin__actions">
              <button className="btn" type="button" onClick={onDone}>
                Sign in
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            {/* The server's own sentence, so the reason they are here is the reason the
                door gave rather than a second telling of it. */}
            <p className="callout" role="status">
              {reason}
            </p>
            <p className="signin__tagline">
              Setting a password for <strong>{email}</strong>
            </p>
            {error ? (
              <p className="problem" role="alert">
                {error}
              </p>
            ) : null}
            <TextField
              id="forced-password"
              label="New password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              secret
              required
              hint={`At least ${minimum} characters.`}
              error={tooShort ? `At least ${minimum} characters.` : undefined}
            />
            <TextField
              id="forced-password-again"
              label="New password again"
              value={again}
              onChange={setAgain}
              autoComplete="new-password"
              secret
              required
              error={mismatch ? "The two passwords do not match." : undefined}
            />
            <div className="signin__actions">
              <button className="btn" type="submit" disabled={busy}>
                {busy ? "Setting…" : "Set password"}
              </button>
            </div>
            <p className="signin__switch">
              <button type="button" className="linkish" onClick={onDone}>
                Back to sign in
              </button>
            </p>
          </form>
        )}
      </div>
    </main>
  );
}

/**
 * The floor the server enforces, restated once here.
 *
 * The link flow reads it from `GET /auth/credential`, which is better because it travels
 * with the answer. This mode has no such call to read it from — there is no token to look
 * up — so it is a constant, and `api/security.py` holds the one the server checks.
 */
const MINIMUM_PASSWORD_LENGTH = 12;

/** The same masthead the sign-in and registration panels carry. */
function Brand() {
  return (
    <>
      <div className="signin__brand">
        <div className="mark" aria-hidden="true">
          c
        </div>
        <div>
          <div className="signin__wm">complao</div>
          <div className="tag">AUDIT PLATFORM</div>
        </div>
      </div>
      <p className="signin__tagline">Stage 1 initial certification · remote</p>
    </>
  );
}
